import React, { useState } from 'react';
import { Calendar, ExternalLink } from 'lucide-react';
import { DndContext, closestCenter, DragEndEvent, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { Task, TaskStatus } from '../../types/task';
import { KanbanContainer, KanbanItem } from '../ui/kanban';
import { Button } from '../ui/button';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface TaskKanbanProps {
  tasks: Task[];
  onTaskDrop: (taskId: number, newStatus: TaskStatus) => Promise<void>;
  onEdit: (task: Task) => void;
}

export const TaskKanban: React.FC<TaskKanbanProps> = ({ 
  tasks, 
  onTaskDrop,
  onEdit
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isDragging, setIsDragging] = useState(false);

  // Configurer les sensors avec un délai pour permettre les clics
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 150,  // Délai en ms avant que le drag commence
        tolerance: 5, // Tolérance de mouvement en px
      },
    })
  );

  // Séparer les tâches par statut
  const todoTasks = tasks.filter(task => task.status === TaskStatus.TODO);
  const inProgressTasks = tasks.filter(task => task.status === TaskStatus.IN_PROGRESS);
  const doneTasks = tasks.filter(task => task.status === TaskStatus.DONE);

  // Gérer le début du glisser-déposer
  const handleDragStart = () => {
    setIsDragging(true);
  };

  // Gérer la fin du glisser-déposer
  const handleDragEnd = (event: DragEndEvent) => {
    setIsDragging(false);
    
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const taskId = Number(active.id);
    const newStatus = over.id as TaskStatus;
    
    const taskToUpdate = tasks.find(task => task.id === taskId);
    
    if (taskToUpdate && taskToUpdate.status !== newStatus) {
      onTaskDrop(taskId, newStatus);
    }
  };

  // Navigation vers la page de détails - mise à jour du chemin
  const navigateToTaskDetails = (taskId: number | undefined) => {
    if (!isDragging && taskId !== undefined) {
      navigate(`/app/tasks/${taskId}`);
    }
  };

  // Rendu d'une carte de tâche
  const renderTaskCard = (task: Task) => (
    <div className="space-y-2">
      <div 
        className="font-medium cursor-pointer hover:text-primary transition-colors"
        onClick={() => task.id !== undefined && navigateToTaskDetails(task.id)}
      >
        {task.title}
      </div>
      <div className="flex justify-between items-center">
        <div className="text-xs text-muted-foreground">
          {task.assignedTo?.name || task.user?.name || 'Non assigné'}
        </div>
        {task.dueDate && (
          <div className="text-xs flex items-center">
            <Calendar className="h-3 w-3 mr-1" />
            {new Date(task.dueDate).toLocaleDateString()}
          </div>
        )}
      </div>
      <div className="flex justify-between pt-1">
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-6 px-2 text-xs"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(task);
          }}
        >
          {t('common.edit')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={(e) => {
            e.stopPropagation();
            task.id !== undefined && navigateToTaskDetails(task.id);
          }}
        >
          <ExternalLink className="h-3 w-3 mr-1" />
          {t('common.view')}
        </Button>
      </div>
    </div>
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[70vh] min-h-[500px]">
        <KanbanContainer
          id="TODO"
          title={t('task.status.todo')}
          variant="todo"
          count={todoTasks.length}
        >
          {todoTasks.map(task => (
            <KanbanItem key={task.id} id={task.id as number}>
              {renderTaskCard(task)}
            </KanbanItem>
          ))}
        </KanbanContainer>
        
        <KanbanContainer
          id="IN_PROGRESS"
          title={t('task.status.in_progress')}
          variant="inProgress"
          count={inProgressTasks.length}
        >
          {inProgressTasks.map(task => (
            <KanbanItem key={task.id} id={task.id as number}>
              {renderTaskCard(task)}
            </KanbanItem>
          ))}
        </KanbanContainer>
        
        <KanbanContainer
          id="DONE"
          title={t('task.status.done')}
          variant="done"
          count={doneTasks.length}
        >
          {doneTasks.map(task => (
            <KanbanItem key={task.id} id={task.id as number}>
              {renderTaskCard(task)}
            </KanbanItem>
          ))}
        </KanbanContainer>
      </div>
    </DndContext>
  );
};
